import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { isoToUTCString } from '@okta/okta-auth-js';
import { MessageService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { PeopleService } from 'src/app/services/people.service';
import { DatePipe } from '@angular/common'

@Component({
  selector: 'ness-person-search',
  templateUrl: './person-search.component.html',
  styles: [
    '.form-group.row { margin-right: -10px; margin-left: -10px;}',
    '[class^="col-"], [class^="offset-"] { padding-right: 10px; padding-left: 10px; }'
  ]
})
export class PersonSearchComponent implements OnInit, OnDestroy {
  @Output() showSearchResults: EventEmitter<boolean> = new EventEmitter();
  private _unsubscribe$: Subject<void> = new Subject<void>();
  readonly peopleSearchStorageKey: string = 'peopleSearchFilterModel';
  maxDateRule: Date = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
  findPersonForm: FormGroup = this.fb.group({
    FilterID: ['', Validators.required],
    FilterInput: '',
    FilterDate: new FormControl<Date>(null),
    FilterSSN: '',
    FilterFirstNameInput: '',
    FilterLastNameInput: '',
    FilterPhoneNumber: ''
  });

  constructor(private fb: FormBuilder,
    public peopleService: PeopleService,
    private messageService: MessageService,
    private elementRef: ElementRef) { }

  ngOnInit(): void {
    this.populateFindPersonForm();
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    this.peopleService.findPeopleResultSubj.next([]);
  }

  public populateFindPersonForm(): void {
    const findPersonFormModel: any = JSON.parse(sessionStorage.getItem(this.peopleSearchStorageKey));
    if (findPersonFormModel) {
      this.findPersonForm.patchValue(findPersonFormModel);
      this.findMatchingPeople();
    }
  }

  public findMatchingPeople(): void {
    this.peopleService.findPeopleResultSubj.next([]);
    if (this.findPersonForm.valid) {
      let filterType: string = this.findPersonForm.get('FilterID').value;
      let filterValue: string = this.lookupFilterValue(filterType);
      if (!!filterValue) {
        let selectedFilterValues: ServerModels.FilterValues = {
          FilterColumnName: filterType,
          FilterValue: filterValue
        } as ServerModels.FilterValues;

        sessionStorage.setItem(this.peopleSearchStorageKey, JSON.stringify(this.findPersonForm.value));
        this.peopleService.findPeople([selectedFilterValues]).pipe(takeUntil(this._unsubscribe$)).subscribe({
          next: (matches: ServerModels.ENESS_PersonDTO[]) => {
            this.peopleService.findPeopleResultSubj.next(matches);
          },
          error: (err:any) => {
            this.showSearchResults.emit(false);
            this.messageService.add({ key: 'peopleMsgs', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage || err.error.Message });
          },
          complete: () => {
            this.showSearchResults.emit(true);
            this.findPersonForm.reset({
              FilterID: ''
            });
          }
        });
      }
    }
  }

  private lookupFilterValue(filterColumn: string): string {
    let filterColumnValue: string = null;
    switch (filterColumn) {
      case 'DateOfBirth':
        filterColumnValue = this.findPersonForm.get('FilterDate').value;
        filterColumnValue = new DatePipe('en-US').transform(filterColumnValue, 'MM/dd/yyyy'); 
        break;
      case 'SSN':
        filterColumnValue = this.findPersonForm.get('FilterSSN').value.trim().replace(/_/g, '');
        break;
      case 'PhoneNumber': 
        let phone: string = this.findPersonForm.get('FilterPhoneNumber').value;
        if (!!phone) {
          phone = phone.trim().replace(/\D/g, ''); // keep only digits
          if (phone.length > 3) {
            filterColumnValue = phone;
          }
        }       
        break;
      case 'Name':
        let firstName: string = this.findPersonForm.get('FilterFirstNameInput').value;
        let lastName: string = this.findPersonForm.get('FilterLastNameInput').value;
        if (!!firstName && !!lastName) {
          filterColumnValue = `${firstName.trim()} ${lastName.trim()}`;
        }
        else if (!!firstName) {
          filterColumnValue = `FirstName:${firstName.trim()}`;
        }
        else if (!!lastName) {
          filterColumnValue = `LastName:${lastName.trim()}`;
        }
        break;
      default:
        filterColumnValue = this.findPersonForm.get('FilterInput').value.trim();
    }

    return filterColumnValue;
  }
}
